import { api, type Page } from "@/lib/api/client";
import type { paths } from "@/lib/api/generated/api";

/** `GET /users/:id/mfa` — generated, so a server change is a type error here. */
export type UserMfaStatus =
  paths["/api/v1/users/{id}/mfa"]["get"]["responses"][200]["content"]["application/json"] extends {
    data: infer T;
  }
    ? T
    : never;
import type {
  GrantRules,
  AdminUser,
  ImpersonateInput,
  ImpersonationCandidate,
  ImpersonationStarted,
  AssignRoleInput,
  AssignmentFilters,
  CreateRoleInput,
  Department,
  EffectiveGrant,
  InviteUserInput,
  InviteUserResult,
  LegalEntity,
  ModuleInstallation,
  OrgUnit,
  PermissionCatalogGroup,
  RequestableModuleStatus,
  Role,
  RoleAssignment,
  RoleFilters,
  ScopedListFilters,
  Site,
  UpdateRoleInput,
  UpdateUserInput,
  UserFilters,
  Warehouse,
} from "./admin.types";

export const adminApi = {
  users: {
    list(filters: UserFilters): Promise<Page<AdminUser>> {
      return api.page<AdminUser>("/users", { params: { ...filters } });
    },
    get(id: string): Promise<AdminUser> {
      return api.get<AdminUser>(`/users/${id}`);
    },
    invite(input: InviteUserInput): Promise<InviteUserResult> {
      return api.post<InviteUserResult>("/users/invite", input);
    },
    update(id: string, input: UpdateUserInput): Promise<AdminUser> {
      return api.patch<AdminUser>(`/users/${id}`, input);
    },
    /** The reason is recorded against the audit entry; the server refuses an empty one. */
    disable(id: string, reason: string): Promise<unknown> {
      return api.post(`/users/${id}/disable`, { reason });
    },
    /** Re-opening asks for a reason too (17 Ekim 2026), as closing always did. */
    enable(id: string, reason: string): Promise<unknown> {
      return api.post(`/users/${id}/enable`, { reason });
    },
    /** The account's second factor as the administrator sees it — never a secret. */
    mfaStatus(id: string): Promise<UserMfaStatus> {
      return api.get<UserMfaStatus>(`/users/${id}/mfa`);
    },
    /**
     * «MFA'yı sıfırla»: the factor and recovery codes go, every session ends. The server asks for
     * a fresh password (`REAUTH_REQUIRED`) — run it through the drawer's `guard`.
     */
    resetMfa(id: string, reason: string): Promise<AdminUser> {
      return api.post<AdminUser>(`/users/${id}/mfa/reset`, { reason });
    },
    /**
     * «Parolayı sıfırla» (31 Ekim 2026): yöneticinin verdiği parola — kişi ilk girişte değiştirmek zorunda,
     * oturumları kapanır. Parola yalnız bu gövdede gidiyor; yanıt hesabın kendisi, parola geri dönmüyor.
     */
    setPassword(id: string, input: { password: string; reason: string }): Promise<AdminUser> {
      return api.post<AdminUser>(`/users/${id}/password`, input);
    },
    unlock(id: string): Promise<unknown> {
      return api.post(`/users/${id}/unlock`);
    },
    /**
     * «Giriş e-postasını değiştir» (kisi-bilgi 19 Ekim 2026): yeni adres + gerekçe. Benzersizlik
     * reddi `fieldErrors.email`; taze parola isteyebilir (`REAUTH_REQUIRED`). Oturumlar düşmez.
     */
    changeLoginEmail(id: string, input: { email: string; reason: string }): Promise<AdminUser> {
      return api.post<AdminUser>(`/users/${encodeURIComponent(id)}/login-email`, input);
    },
    /**
     * Davet bekleyen (`INVITED`) hesaba yeni bir davet çıkarır; öncekiler geçersiz olur. Cevap
     * `invite` ile aynı: e-posta gitmediyse kod BİR KEZ burada döner. Sunucu bu uca 9 Ekim 2026'ya
     * kadar sahipti ama ekran onu hiç çağırmıyordu — süresi dolan davetin çaresi yoktu.
     */
    resendInvite(id: string): Promise<Omit<InviteUserResult, "user">> {
      return api.post<Omit<InviteUserResult, "user">>(`/users/${id}/invite/resend`);
    },
  },

  roles: {
    list(filters: RoleFilters): Promise<Page<Role>> {
      return api.page<Role>("/authorization/roles", { params: { ...filters } });
    },
    get(id: string): Promise<Role> {
      return api.get<Role>(`/authorization/roles/${id}`);
    },
    /** The permission catalogue — code-defined, seeded at boot, the same list every time. */
    catalog(): Promise<PermissionCatalogGroup[]> {
      return api.get<PermissionCatalogGroup[]>("/authorization/roles/catalog");
    },
    /** Kısıtlı anahtarlar, gerekçe eşiği ve «ben SYSTEM_ADMIN miyim» — ekranın gizleme kuralı (K4). */
    grantRules(): Promise<GrantRules> {
      return api.get<GrantRules>("/authorization/roles/grant-rules");
    },
    create(input: CreateRoleInput): Promise<Role> {
      return api.post<Role>("/authorization/roles", input);
    },
    update(id: string, input: UpdateRoleInput): Promise<Role> {
      return api.patch<Role>(`/authorization/roles/${id}`, input);
    },
    remove(id: string): Promise<void> {
      return api.delete(`/authorization/roles/${id}`);
    },
  },

  assignments: {
    list(filters: AssignmentFilters): Promise<Page<RoleAssignment>> {
      return api.page<RoleAssignment>("/authorization/assignments", { params: { ...filters } });
    },
    create(input: AssignRoleInput): Promise<RoleAssignment> {
      return api.post<RoleAssignment>("/authorization/assignments", input);
    },
    revoke(id: string, reason: string): Promise<unknown> {
      return api.post(`/authorization/assignments/${id}/revoke`, { reason });
    },
    effective(userId: string): Promise<EffectiveGrant[]> {
      return api.get<EffectiveGrant[]>(`/authorization/assignments/effective/${userId}`);
    },
  },

  modules: {
    /** One page is the whole catalogue; every row carries its manifest (name, layer, `dependsOn`). */
    list(): Promise<Page<ModuleInstallation>> {
      return api.page<ModuleInstallation>("/configuration/modules", { params: { limit: 100 } });
    },
    setStatus(
      id: string,
      input: { status: RequestableModuleStatus; reason?: string | undefined },
    ): Promise<ModuleInstallation> {
      return api.patch<ModuleInstallation>(`/configuration/modules/${id}`, input);
    },

    /**
     * The departments a module catalogue implies, and who runs each.
     *
     * Under `modules` rather than beside `roles` because a department is what switching a
     * module on CREATES — the two halves of one fact, and the server gates them on the same
     * key for the same reason. Not paginated: the row count is the catalogue's.
     */
    departments(): Promise<Department[]> {
      return api.get<Department[]>("/configuration/modules/departments");
    },
  },

  /**
   * Support windows: signing in AS somebody, to see what they see.
   *
   * `start` needs no token handling here — the server swaps the access cookie. `end` is called
   * only AFTER `returnToSelf`, because a support session resolves permissions against the
   * TARGET, and the person whose account was entered almost never holds
   * `identity.session.revoke`.
   */
  impersonation: {
    /** «Kullanıcı değiştir»in listesi: ad, birim ve rollerle; kendiniz ve yönetim hesapları yok (21 Ekim 2026). */
    candidates(filters: { q?: string | undefined; limit?: number | undefined }): Promise<ImpersonationCandidate[]> {
      return api.get<ImpersonationCandidate[]>("/support/impersonation/candidates", { params: { ...filters } });
    },
    start(input: ImpersonateInput): Promise<ImpersonationStarted> {
      return api.post<ImpersonationStarted>("/support/impersonation", input);
    },
    end(id: string): Promise<unknown> {
      return api.post(`/support/impersonation/${id}/end`);
    },
    /**
     * Back to your own identity.
     *
     * The refresh cookie was never swapped — it still belongs to the administrator's own
     * session — so refreshing mints a normal token for them and the support token is simply
     * replaced. This is also what happens by itself when the window expires.
     */
    returnToSelf(): Promise<unknown> {
      return api.post("/auth/refresh", {});
    },
  },

  organization: {
    legalEntities(filters: ScopedListFilters): Promise<Page<LegalEntity>> {
      return api.page<LegalEntity>("/organization/legal-entities", { params: { ...filters } });
    },
    sites(filters: ScopedListFilters): Promise<Page<Site>> {
      return api.page<Site>("/organization/sites", { params: { ...filters } });
    },
    orgUnits(filters: ScopedListFilters): Promise<Page<OrgUnit>> {
      return api.page<OrgUnit>("/organization/org-units", { params: { ...filters } });
    },
  },

  warehouses: {
    list(filters: ScopedListFilters): Promise<Page<Warehouse>> {
      return api.page<Warehouse>("/inventory/warehouses", { params: { ...filters } });
    },
  },
};
